import Maybe from 'true-myth/maybe';
import type Result from 'true-myth/result';

import type {
    Customization,
    CustomizationProvider,
    CustomizationProviderOptions,
} from '~/shared/customization/customization';
import { customizationSchema } from '~/shared/customization/customization';
import { idSchema } from '~/shared/data-contract/id';
import { HTTPError } from '~/shared/http-fetcher/http-error';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import { createCustomizationState } from './customization-state';
import { TenantNotFoundError } from './tenant-not-found-error';

const idSchemaMaybeTransform = idSchema.nullable().transform((id) => {
    return Maybe.of(id);
});

const getCustomizationParametersFromLocationHash = (
    location: Location,
): { readonly assumedTenantId: Maybe<string>; readonly customizationId: Maybe<string> } => {
    const params = new URLSearchParams(location.hash.substring('#customizationConfig='.length));

    return {
        assumedTenantId: idSchemaMaybeTransform.parse(params.get('assumedTenantId')),
        customizationId: idSchemaMaybeTransform.parse(params.get('customizationId')),
    };
};

export type StorageKey = 'assumedTenantId' | 'customizationId';

export type CustomStorage = Storage & {
    readonly getItem: (key: StorageKey) => string | null;
    readonly removeItem: (key: StorageKey) => void;
    readonly setItem: (key: StorageKey, value: string) => void;
};

export type CobrainerCustomizationProviderDependencies = {
    readonly storage: CustomStorage;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createCobrainerCustomizationProvider = async (
    { storage, validatingCobrainerApiFetcher }: CobrainerCustomizationProviderDependencies,
    options: CustomizationProviderOptions,
): Promise<CustomizationProvider | never> => {
    const { location } = options;
    const state = createCustomizationState();
    const customizationParams = getCustomizationParametersFromLocationHash(location);
    const { assumedTenantId, customizationId } = customizationParams;

    if (assumedTenantId.isJust || customizationId.isJust) {
        if (assumedTenantId.isJust) {
            storage.setItem('assumedTenantId', assumedTenantId.value);
        } else {
            storage.removeItem('assumedTenantId');
        }

        if (customizationId.isJust) {
            storage.setItem('customizationId', customizationId.value);
        } else {
            storage.removeItem('customizationId');
        }
    }

    const getIdFromStorage = (key: StorageKey): Maybe<string> => idSchemaMaybeTransform.parse(storage.getItem(key));

    const determinedTenantId = getIdFromStorage('assumedTenantId');
    const determinedCustomizationId = getIdFromStorage('customizationId');

    state.update({ assumedTenantId: determinedTenantId });

    // Dev servers accessed through Tailscale Serve run under a *.ts.net hostname,
    // which is not a registered tenant slug; resolve the tenant as if on localhost.
    const tenantSlug = import.meta.env.DEV && location.hostname.endsWith('.ts.net') ? 'localhost' : location.hostname;

    const customization: Result<Customization, Error> = await validatingCobrainerApiFetcher.get(customizationSchema, {
        pathname: `/customizations/slug/${tenantSlug}/default`,
    });

    if (customization.isErr) {
        if (customization.error instanceof HTTPError && customization.error.response.status === 404) {
            throw new TenantNotFoundError(tenantSlug);
        }

        throw customization.error;
    }

    const overrideCustomizationPath: Maybe<string> = determinedCustomizationId
        .map((id) => `id/${id}`)
        .or(determinedTenantId.map((id) => `tenant/${id}/default`));

    const overrideCustomization: Result<Customization, Error> = overrideCustomizationPath.isJust
        ? await validatingCobrainerApiFetcher.get(customizationSchema, {
              pathname: `/customizations/${overrideCustomizationPath.value}`,
          })
        : customization;

    if (overrideCustomization.isErr) {
        throw overrideCustomization.error;
    }

    const effectiveCustomization = Maybe.of({
        ...overrideCustomization.value,
        oidcConfiguration: customization.value.oidcConfiguration,
    });

    state.update({
        customization: effectiveCustomization,
        isDefaultCustomization: overrideCustomization.value.isDefault,
    });

    return {
        get assumedTenantId() {
            return state.get('assumedTenantId');
        },
        get customization() {
            return state.get('customization');
        },
        get isDefaultCustomization() {
            return state.get('isDefaultCustomization');
        },
        reset() {
            storage.removeItem('customizationId');
            storage.removeItem('assumedTenantId');
            state.reset();
        },
    };
};
